import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { ISelectOption } from '../../../selectOptions';
import { ISelectFilter } from '../../interfaces/selectFilter.interface';

/**
 * implementation for dynamic multi select filter.
 */
export class DynamicMultiSelectFilters implements ISelectFilter {
  private readonly _options$: BehaviorSubject<ISelectOption[]>;
  private _filterIds: Set<string>;
  private _optionsById: _.Dictionary<ISelectOption>;
  private readonly _filterType?: string;

  constructor(options: ISelectOption[] = [], filterIdsStartingValue: string[] = [], filterType?: string) {
    this._options$ = new BehaviorSubject(options);
    this._optionsById = _.keyBy(options, 'id');
    this._filterIds = new Set(filterIdsStartingValue);
    this._filterType = filterType;
  }

  public updateIfNeeded(selectedOptions: ISelectOption[]): boolean {
    const newSelectedOptionsIds = Object.keys(_.keyBy(selectedOptions, 'id'));

    if (!this._shouldUpdateSelectedOptions(newSelectedOptionsIds)) {
      this._filterIds = new Set(newSelectedOptionsIds);

      this._options$.next(_.union(this._options$.value, selectedOptions));
      this._options$.value.forEach((option) => {
        this._optionsById[option.id] = option;
      });
      return true;
    }

    return false;
  }

  public getSelectedFiltersIds(): string[] {
    return Array.from(this._filterIds);
  }

  public getSelectedFiltersValues(): string[] {
    const values: string[] = [];

    // If options haven't loaded yet but we have filter IDs, they might be VALUES from URL params
    // Return them as-is so they can be used in the API call
    if (this._options$.value.length == 0) {
      return Array.from(this._filterIds);
    }

    this._filterIds.forEach((id) => {
      if (this._optionsById[id]) {
        values.push(this._optionsById[id].value);
      }
    });
    return values;
  }

  public getSelectedOptions(): ISelectOption[] {
    const options: ISelectOption[] = [];

    this._filterIds.forEach((selectedId) => {
      if (selectedId in this._optionsById) {
        // Option is fully loaded, use it
        options.push(this._optionsById[selectedId]);
      } else if (this._options$.value.length == 0 && this._filterType) {
        // Options haven't been fetched yet, create placeholder option with filter type
        // This ensures chips appear immediately when loading from URL
        options.push({
          id: selectedId,
          value: selectedId,
          displayName: selectedId,
          filterType: this._filterType,
          categoryName: this._filterType,
          withoutBeautifyDisplayName: true,
        } as ISelectOption);
      }
    });

    return options;
  }

  public getOptions$(): BehaviorSubject<ISelectOption[]> {
    return this._options$;
  }

  public updateOptions(options: ISelectOption[]): boolean {
    const fistTimeOptions: boolean = this._options$.value.length === 0;
    const mergedOptions = _.unionBy(this._options$.getValue(), options, 'id');
    this._options$.next(mergedOptions);
    this._optionsById = _.keyBy(mergedOptions, 'id'); // FIX: use merged options, not just new options

    // If this is the first time loading options and we have filterIds that were set from URL params,
    // they might be VALUES instead of IDs. We need to convert them.
    if (fistTimeOptions && this._filterIds.size > 0) {
      const optionsByValue = _.keyBy(options, 'value');
      const convertedIds: string[] = [];

      this._filterIds.forEach((filterIdOrValue) => {
        // Check if it's an ID
        if (filterIdOrValue in this._optionsById) {
          convertedIds.push(filterIdOrValue);
        }
        // Check if it's a VALUE that needs to be converted to ID
        else if (filterIdOrValue in optionsByValue) {
          convertedIds.push(optionsByValue[filterIdOrValue].id);
        }
        // For dynamic filters, the value might be valid even without options loaded
        // Keep it so it can be fetched later
        else {
          convertedIds.push(filterIdOrValue);
        }
      });

      if (convertedIds.length > 0) {
        this._filterIds = new Set(convertedIds);
        return true;
      }
    }

    // filter out old selected options if not relevant with new selection options
    const newSelectedOptions: string[] = [];

    this._filterIds.forEach((filterId) => {
      if (filterId in this._optionsById) {
        newSelectedOptions.push(filterId);
      }
    });

    if (fistTimeOptions && newSelectedOptions.length > 0) {
      return true;
    }

    // update filter ids if selected options should be updated
    if (!this._shouldUpdateSelectedOptions(newSelectedOptions)) {
      this._filterIds = new Set(newSelectedOptions);
      return true;
    }

    return false;
  }

  public updateByValuesIfNeeded(values: string[]): boolean {
    const optionsOfReceivedValues: ISelectOption[] = [];

    if (this._filterIds.size > 0 && values.length == 0) {
      this._filterIds = new Set([]);
      return true;
    }

    if (this._options$.value.length == 0) {
      // Store values temporarily - they will be converted to IDs when options load
      this._filterIds = new Set<string>(values);
      // Return true to trigger an update when options become available
      return true;
    }

    this._options$.value.forEach((option) => {
      const index = values.indexOf(option.value);
      if (index !== -1) {
        optionsOfReceivedValues.push(option);
      }
    });

    if (optionsOfReceivedValues.length > 0) {
      return this.updateIfNeeded(optionsOfReceivedValues);
    }

    return false;
  }

  private _shouldUpdateSelectedOptions(newSelectedOptionsIds: string[]): boolean {
    return _.isEqual(Array.from(this._filterIds).sort(), newSelectedOptionsIds.sort());
  }
}
